import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  LineChart, BarChart2, Bot, Bell, Link2,
  TrendingUp, ChevronRight, Wallet, Gauge,
  Webhook, BellRing, Boxes, ArrowRightLeft,
  Zap, Repeat, AlertTriangle, Share2, X,
  Search, BookOpen, ExternalLink, Eye, Table,
  CircleDollarSign, ArrowDownUp, Globe, Users,
  Layers, Activity, Filter, Copy, Rocket, Code,
  MessageSquare, Medal, Landmark, Target, Cpu,
  BadgeDollarSign, PieChart, Coins, BarChart4, Edit2,
  BarChart3, Shield, ArrowUpDown, Lock, Waves, Settings, Map
} from 'lucide-react';
import { Position, CanvasWidget, MenuCategory, MenuItem } from './types';
import { 
  createAutoTraderWidget,
  createWhaleAlertsWidget,
  createAdvancedChartsWidget,
  createPortfolioWidget,
  createRuleBasedTraderWidget,
  createCopyTraderWidget,
  createWalletStatsWidget,
  createTradesBuySellWidget,
  createProfitAndLossWidget,
  createTokenStatsWidget,
  createOrderBookWidget,
  createAdvancedAutoTraderWidget,
  createTokenInfoWidget,
  createActivePositionsWidget,
  createSniperBotWidget,
  createRugTrackerWidget,
  createWhaleTrackerWidget,
  createSmartWatchlistWidget,
  createEKKOmapWidget,
  createBundleScannerWidget
} from '../../widgets';
import { createWalletManagerWidget } from '../../widgets/WalletManager/WalletManager';
import { ekkoColors } from '../../styles/globalWidgetStyles';

// Custom styles for the context menu - Light Theme
const contextMenuStyles = `
  .ekko-context-menu {
    background-color: rgb(255, 255, 255);
    border: 1px solid rgba(229, 231, 235, 0.5);
    border-radius: 0.75rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(36, 195, 91, 0.1);
    overflow: hidden;
    animation: fadeIn 0.15s ease-out;
    backdrop-filter: blur(12px);
  }

  .ekko-context-menu::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  .ekko-context-menu::-webkit-scrollbar-track {
    background: transparent;
  }

  .ekko-context-menu::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.3);
    border-radius: 2px;
  }

  .ekko-context-menu::-webkit-scrollbar-thumb:hover {
    background-color: #24c35b;
  }

  .ekko-context-menu .category-menu {
    border-right: 1px solid rgba(229, 231, 235, 0.5);
    background: rgb(249, 250, 251);
  }

  .ekko-context-menu .category-item {
    transition: background-color 0.15s, border-color 0.15s;
    color: rgb(55, 65, 81);
    background: rgb(249, 250, 251);
  }

  .ekko-context-menu .category-item:hover {
    background-color: rgb(243, 244, 246);
    border-color: #24c35b;
    color: #24c35b;
  }

  .ekko-context-menu .category-item.active {
    background-color: rgba(36, 195, 91, 0.1);
    border-color: #24c35b;
    color: #24c35b;
  }

  .ekko-context-menu .submenu-item {
    transition: background-color 0.15s;
    background: rgb(255, 255, 255);
    color: rgb(55, 65, 81);
  }

  .ekko-context-menu .submenu-item:hover {
    background-color: rgba(36, 195, 91, 0.05);
    border-color: rgba(36, 195, 91, 0.2);
    color: rgb(55, 65, 81);
  }

  .ekko-context-menu .submenu-item .item-icon {
    color: rgb(107, 114, 128);
  }

  .ekko-context-menu .submenu-item:hover .item-icon,
  .ekko-context-menu .submenu-item:hover .item-title {
    color: #24c35b;
  }

  .ekko-context-menu .submenu-item.highlighted .item-title {
    color: #24c35b;
    font-weight: 500;
  }

  .ekko-context-menu .submenu-item .item-description {
    color: rgb(107, 114, 128);
  }

  .ekko-context-menu .submenu {
    background: rgb(255, 255, 255);
    border-left: 1px solid rgba(229, 231, 235, 0.5);
  }

  .ekko-context-menu .p-2.bg-[#131318],
  .ekko-context-menu .p-2.bg-[#131318].border-b {
    background: rgb(249, 250, 251) !important;
    border-bottom: 1px solid rgba(229, 231, 235, 0.5) !important;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.98);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

interface ContextMenuProps {
  position: Position;
  onClose: () => void;
  onAddWidget: (widget: Partial<CanvasWidget>) => void;
  zoom: number;
  panOffset?: Position;
}

const menuCategories: MenuCategory[] = [
  {
    name: 'Trading/Market',
    icon: <LineChart size={14} className="text-[#24c35b]" />,
    items: [
      {
        id: 'trades-buy-sell-widget',
        name: 'Buy/Sell',
        icon: <ArrowDownUp size={14} />,
        description: 'Dedicated buy and sell interface for token trading',
        highlighted: true
      },
      {
        id: 'advanced-charts-widget',
        name: 'Advanced Charts',
        icon: <TrendingUp size={14} />,
        description: 'Interactive token price charts with search functionality and advanced analytics',
        highlighted: true
      },
      {
        id: 'token-info-widget',
        name: 'Token Info',
        icon: <BookOpen size={14} />,
        description: 'Detailed information for a specific token',
        highlighted: true
      },
      {
        id: 'token-stats-widget',
        name: 'Token Stats',
        icon: <BarChart2 size={14} />,
        description: 'Comprehensive token statistics and analytics',
        highlighted: true
      },
      {
        id: 'order-book-widget',
        name: 'Order Book',
        icon: <Boxes size={14} />,
        description: 'Real-time transactions for a specific token',
        highlighted: true
      },
      {
        id: 'rug-tracker-widget',
        name: 'Rug Tracker',
        icon: <AlertTriangle size={14} />,
        description: 'Track tokens where developers have sold their holdings',
        highlighted: true
      },
      {
        id: 'whale-tracker-widget',
        name: 'Whale Tracker',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 9.1V5a2 2 0 0 0-4 0" />
            <path d="M18 5a2 2 0 0 1 4 0" />
            <path d="M6 9.7 3.9 8.4C2.7 7.7 2 6.4 2 5V3c2 0 4 2 4 2s2-2 4-2v2c0 1.4-.7 2.7-1.9 3.4l-3.8 2.4A5 5 0 0 0 7 20h12c1.7 0 3-1.3 3-3v-3c0-2.8-2.2-5-5-5-2.7 0-5.1 1.4-6.4 3.6L9.7 14A2 2 0 0 1 6 13Z" />
            <path d="M15 15h.01" />
          </svg>
        ),
        description: 'Monitor large transactions and whale movements',
        highlighted: true
      },
    ]
  },
  {
    name: 'Automated Bots',
    icon: <Bot size={14} className="text-[#24c35b]" />,
    items: [
      {
        id: 'sniper-bot-widget',
        name: 'Sniper Bot',
        icon: <Target size={14} />,
        description: 'Real-time token monitoring and automated sniping',
        highlighted: true
      },
      {
        id: 'copy-trader-widget',
        name: 'Copy Trader',
        icon: <Copy size={14} />,
        description: 'Automatically copy successful traders\' strategies',
        highlighted: true
      },
      {
        id: 'advanced-auto-trader-widget',
        name: 'Auto Trader',
        icon: <Bot size={14} />,
        description: 'Configure automatic trading with multiple strategies and rules',
        highlighted: true
      },
    ]
  },
  {
    name: 'Portfolio & Wallets',
    icon: <Coins size={14} className="text-[#24c35b]" />,
    items: [
      {
        id: 'wallet-manager-widget',
        name: 'Wallet Manager',
        icon: <Settings size={14} />,
        description: 'Comprehensive wallet management, transfers, and private key backup',
        highlighted: true
      },
      {
        id: 'profit-and-loss-widget',
        name: 'Profit & Loss',
        icon: <TrendingUp size={14} />,
        description: 'Track your portfolio profit and loss with detailed metrics',
        highlighted: true
      },
      {
        id: 'active-positions-widget',
        name: 'Active Positions',
        icon: <Wallet size={14} />,
        description: 'View your active token positions and their performance',
        highlighted: true
      },
      {
        id: 'smart-watchlist-widget',
        name: 'Smart Watchlist',
        icon: <Eye size={14} />,
        description: 'Track tokens and wallets with custom notes, alerts, and priorities',
        highlighted: true
      }
    ]
  },
  {
    name: 'Utilities',
    icon: <Settings size={14} className="text-[#24c35b]" />,
    items: [
      {
        id: 'bundle-scanner-widget',
        name: 'Bundle Scanner',
        icon: (
          <div className="flex items-center gap-1">
            <Search size={10} />
            <img 
              src="https://trench.bot/logo.webp" 
              alt="TrenchRadar" 
              className="h-5 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        ),
        description: 'Bundle analysis and tracking powered by TrenchRadar',
        highlighted: true
      }
    ]
  },
];

// Helper function to check if a category contains highlighted items
const categoryHasHighlightedItems = (category: MenuCategory) => {
  return category.items.some(item => item.highlighted);
};

// Add an isUnlocked function to check if a menu item should be unlocked
const isUnlocked = (itemId: string): boolean => {
  // Define which items should be unlocked
  const unlockedItems = [
    'advanced-charts-widget',
    'trades-buy-sell-widget',
    'profit-and-loss-widget',
    'token-stats-widget',
    'token-info-widget',
    'order-book-widget',
    'auto-trader-widget',
    'advanced-auto-trader-widget',
    'sniper-bot-widget',
    'copy-trader-widget',
    'smart-watchlist-widget',
    'wallet-manager-widget',
    'ekko-map-widget',
    'bundle-scanner-widget'
  ];
  
  return unlockedItems.includes(itemId);
};

// Define an extended position type for the submenu position
interface SubmenuPosition {
  top: number;
  left: number;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ 
  position, 
  onClose, 
  onAddWidget, 
  zoom,
  panOffset = { x: 0, y: 0 } 
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<SubmenuPosition | null>(null);
  const [submenuVisible, setSubmenuVisible] = useState(false);
  const [submenuHover, setSubmenuHover] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<MenuItem[] | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (categoryName: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setActiveCategory(categoryName);
    setSubmenuPosition({
      top: rect.top,
      left: rect.right + 1
    });
    
    // Find the category's items
    const category = menuCategories.find(c => c.name === categoryName);
    if (category) {
      setActiveSubmenu(category.items);
    }
    
    // Show submenu
    setSubmenuVisible(true);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setActiveCategory(null);
      setSubmenuPosition(null);
    }, 100);
  };

  const handleCategoryClick = (categoryName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    
    // Toggle the active category
    if (activeCategory === categoryName) {
      setActiveCategory(null);
      setSubmenuPosition(null);
    } else {
      setActiveCategory(categoryName);
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 1
      });
    }
  };

  const handleSubmenuMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleSubmenuMouseLeave = () => {
    handleMouseLeave();
  };

  const handleAddWidget = (category: MenuCategory, item: MenuItem) => {
    // Position is already relative to the canvas container, so we can use it directly
    // We just need to account for the canvas transform to place widgets correctly
    const canvasX = (position.x - panOffset.x) / zoom;
    const canvasY = (position.y - panOffset.y) / zoom;

    // Add console logging for debugging
    console.log(`Adding widget ${item.id} at canvas position: (${canvasX}, ${canvasY})`);
    console.log(`Original position: (${position.x}, ${position.y}), panOffset: (${panOffset.x}, ${panOffset.y}), zoom: ${zoom}`);

    // Special handling for specific widget types
    switch (item.id) {
      case 'auto-trader-widget': {
        const widget = createAutoTraderWidget();
        console.log('AutoTrader widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'advanced-auto-trader-widget': {
        const widget = createAdvancedAutoTraderWidget();
        console.log('Advanced Auto Trader widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'sniper-bot-widget': {
        const widget = createSniperBotWidget(onAddWidget);
        console.log('SniperBot widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'whale-alerts-widget': {
        const widget = createWhaleAlertsWidget();
        console.log('WhaleAlerts widget created:', widget);
        
        // Add a backup fallback content in case the main content is causing issues
        const backupContent = (
          <div className="flex flex-col items-center justify-center h-full">
            <BadgeDollarSign size={48} className="mb-4 text-[#24c35b]" />
                          <h2 className="text-xl mb-2 text-gray-900">Whale Alerts</h2>
              <p className="text-gray-600">Loading transaction data...</p>
          </div>
        );
        
        onAddWidget({
          ...widget,
          content: widget.content || backupContent,  // Use a backup if content is undefined
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'advanced-charts-widget': {
        const widget = createAdvancedChartsWidget();
        console.log('AdvancedCharts widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'token-charts-widget': {
        const widget = createAdvancedChartsWidget();
        console.log('AdvancedCharts widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'portfolio-widget': {
        const widget = createPortfolioWidget();
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'rule-based-trader-widget': {
        const widget = createRuleBasedTraderWidget();
        console.log('RuleBasedTrader widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'copy-trader-widget': {
        const widget = createCopyTraderWidget(onAddWidget);
        console.log('CopyTrader widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'wallet-stats-widget': {
        const widget = createWalletStatsWidget();
        console.log('WalletStats widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'trades-buy-sell-widget': {
        const widget = createTradesBuySellWidget();
        console.log('TradesBuySell widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'profit-and-loss-widget': {
        const widget = createProfitAndLossWidget();
        console.log('ProfitAndLoss widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'token-stats-widget': {
        const widget = createTokenStatsWidget();
        console.log('TokenStats widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'order-book-widget': {
        const widget = createOrderBookWidget();
        console.log('OrderBook widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }
      
      case 'token-info-widget': {
        const widget = createTokenInfoWidget();
        console.log('TokenInfo widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'active-positions-widget': {
        const widget = createActivePositionsWidget();
        console.log('ActivePositions widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'rug-tracker-widget': {
        const widget = createRugTrackerWidget();
        console.log('RugTracker widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'whale-tracker-widget': {
        const widget = createWhaleTrackerWidget();
        console.log('WhaleTracker widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'smart-watchlist-widget': {
        const widget = createSmartWatchlistWidget();
        console.log('SmartWatchlist widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'wallet-manager-widget': {
        const widget = createWalletManagerWidget(onAddWidget);
        console.log('WalletManager widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'ekko-map-widget': {
        const widget = createEKKOmapWidget();
        console.log('EKKOmap widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

      case 'bundle-scanner-widget': {
        const widget = createBundleScannerWidget();
        console.log('BundleScanner widget created:', widget);
        onAddWidget({
          ...widget,
          position: { x: canvasX, y: canvasY }
        });
        onClose();
        return;
      }

    }

    // For other widgets, create a default widget with guaranteed content
    onAddWidget({
      type: item.id,
      title: item.name,
      position: { x: canvasX, y: canvasY },
      size: { width: 350, height: 300 },
      content: (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className={`w-12 h-12 mx-auto rounded-lg bg-[#24c35b]/20 flex items-center justify-center mb-2`}>
              {item.icon}
            </div>
                          <h3 className="text-sm font-medium mb-1 text-gray-900">{item.name}</h3>
              <p className="text-xs text-gray-600 max-w-[250px]">{item.description || 'Coming soon'}</p>
          </div>
        </div>
      )
    });
    
    onClose();
  };

  // Render the context menu
  return createPortal(
    <>
      <style>{contextMenuStyles}</style>
      <div 
        className="fixed inset-0 z-[9998]" 
        onClick={onClose}
      />
      <div 
        className="ekko-context-menu fixed flex z-[9999] max-h-[80vh] w-[800px]"
        style={{
          left: position.x,
          top: position.y,
          transform: `translate(-20px, -20px)`
        }}
        ref={menuRef}
      >
        {/* Category menu */}
        <div className="category-menu w-[200px] overflow-y-auto max-h-[80vh]">
          <div className="p-2 bg-gray-50">
            <div className="text-xs text-gray-600 uppercase tracking-wider px-2 py-1">
              Widget Categories
            </div>
          </div>
          
          <div className="p-2">
        {menuCategories.map((category) => (
          <div
            key={category.name} 
                className={`category-item p-2 rounded cursor-pointer mb-1 border border-transparent flex items-center ${
                  activeCategory === category.name ? 'active' : ''
                }`}
                onClick={(e) => handleCategoryClick(category.name, e)}
            onMouseEnter={(e) => handleMouseEnter(category.name, e)}
          >
                <div className="mr-2 text-[#24c35b]">{category.icon}</div>
                <div className="flex-1 text-sm">{category.name}</div>
                <ChevronRight size={16} className="text-gray-600" />
              </div>
            ))}
          </div>
        </div>

        {/* Submenu */}
        {activeSubmenu && (
              <div
            className="submenu flex-1 overflow-y-auto max-h-[80vh] bg-white"
                onMouseEnter={handleSubmenuMouseEnter}
                onMouseLeave={handleSubmenuMouseLeave}
              >
            <div className="p-2 bg-gray-50 border-b border-gray-200/30">
              <div className="text-xs text-gray-600 uppercase tracking-wider px-2 py-1">
                {activeCategory} Widgets
              </div>
            </div>
            
            <div className="p-2 grid grid-cols-2 gap-2">
              {activeSubmenu.map((item) => (
                <div
                  key={item.id}
                  className={`submenu-item p-3 rounded cursor-pointer border border-transparent hover:border-[#24c35b]/20 ${
                    item.highlighted ? 'highlighted' : ''
                  } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => !item.disabled && handleAddWidget(menuCategories.find(c => c.name === activeCategory)!, item)}
                >
                  <div className="flex items-center mb-1">
                    <div className="item-icon mr-2">{item.icon}</div>
                    <div className="flex-1 text-sm font-medium">{item.name}</div>
                    {item.highlighted && (
                      <div className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-[#24c35b]/10 text-[#24c35b]">
                        New
                      </div>
                    )}
                  </div>
                  <div className="item-description text-xs text-gray-400">
                    {item.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default ContextMenu;